'use client';

import type { FC } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import { getLevelGradientClass, getLevelIcon } from '../LevelBadge';
import { Avatar } from '../ui/Avatar';

interface UserAvatarProps {
  userId: string | number;
  firstName?: string | null;
  lastName?: string | null;
  level?: number;
  size?: 's' | 'm' | 'l' | 'tied' | 'xl';
  className?: string;
}

const avatarBorderVariants = cva(
  'rounded-full overflow-hidden',
  {
    variants: {
      size: {
        s: 'w-8 h-8 p-0.5',
        m: 'w-12 h-12 p-0.5',
        l: 'w-16 h-16 p-[3px]',
        tied: 'w-[60px] h-[60px] p-[3px]',
        xl: 'w-[120px] h-[120px] p-1',
      },
    },
    defaultVariants: {
      size: 's',
    },
  }
);

const levelBadgeVariants = cva(
  'absolute left-1/2 -translate-x-1/2 flex items-center rounded-full shadow-[0_2px_6px_rgba(0,0,0,0.3)] text-white whitespace-nowrap',
  {
    variants: {
      size: {
        s: '-bottom-1 py-0.5 px-1 gap-px',
        m: '-bottom-1.5 py-[3px] px-1.5 gap-0.5',
        l: '-bottom-2 py-1 px-2 gap-0.5',
        tied: '-bottom-1.5 py-1 px-2.5 gap-[3px]',
        xl: '-bottom-2 py-1.5 px-3.5 gap-1 shadow-card',
      },
    },
    defaultVariants: {
      size: 's',
    },
  }
);

const levelIconVariants = cva(
  'text-white',
  {
    variants: {
      size: {
        s: 'text-[8px]',
        m: 'text-[10px]',
        l: 'text-xs',
        tied: 'text-[10px]',
        xl: 'text-sm',
      },
    },
    defaultVariants: {
      size: 's',
    },
  }
);

const levelTextVariants = cva(
  'font-bold text-white',
  {
    variants: {
      size: {
        s: 'text-[8px]',
        m: 'text-[10px]',
        l: 'text-xs',
        tied: 'text-sm',
        xl: 'text-lg',
      },
    },
    defaultVariants: {
      size: 's',
    },
  }
);

export const UserAvatar: FC<UserAvatarProps> = ({ userId, firstName, lastName, level, size = 's', className = '' }) => {
  const levelGradientClass = level != null ? getLevelGradientClass(level) : undefined;
  const LevelIcon = level != null ? getLevelIcon(level) : null;
  const avatarUrl = `${getApiBaseUrl()}/api/users/avatar/${userId}`;
  const innerSize = size === 'tied' ? 'm' : size === 'l' ? 'l' : size === 'xl' ? 'xl' : size === 'm' ? 'm' : 's';

  return (
    <div className={cn('relative inline-flex shrink-0', className)}>
      <div className={cn(avatarBorderVariants({ size }), levelGradientClass)}>
        <Avatar
          src={avatarUrl}
          size={innerSize}
          fallbackKey={userId}
          firstName={firstName}
          lastName={lastName}
          className="w-full h-full"
        />
      </div>
      {level != null && LevelIcon && (
        <div className={cn(levelBadgeVariants({ size }), levelGradientClass)}>
          <LevelIcon className={levelIconVariants({ size })} />
          <span className={levelTextVariants({ size })}>{level}</span>
        </div>
      )}
    </div>
  );
};
